/**
 * TaxIdInput — country-aware fiscal identifier composite input.
 *
 * Renders a type selector + value input. The selected type is stored on the
 * record's `tax_id_type` field; the value on the existing `tax_id` (or `trn`)
 * field. Backwards-compatible: if a record only has `trn` populated, the
 * type defaults to `TRN_UAE`.
 *
 * Props:
 *   typeValue       string  current tax_id_type key
 *   valueValue      string  current tax_id value
 *   onTypeChange    (key) => void
 *   onValueChange   (str) => void
 *   country?        string  used to pre-select the most likely type when type is empty
 *   testIdPrefix?   string  data-testid root
 */
import React from 'react';
import { Input } from '../ui/input';
import { TAX_ID_TYPES, TAX_ID_BY_KEY, defaultTaxIdForCountry } from '../../config/taxIdTypes';

export const TaxIdInput = ({
  typeValue,
  valueValue,
  onTypeChange,
  onValueChange,
  country,
  testIdPrefix = 'tax-id',
  required = false,
}) => {
  const effectiveType = typeValue || defaultTaxIdForCountry(country);
  const meta = TAX_ID_BY_KEY[effectiveType] || TAX_ID_BY_KEY.OTHER;

  return (
    <div className="grid grid-cols-3 gap-2">
      <div>
        <select
          data-testid={`${testIdPrefix}-type`}
          value={effectiveType}
          onChange={(e) => onTypeChange(e.target.value)}
          className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm
            focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
          title="Select identifier type">
          {TAX_ID_TYPES.map((t) => (
            <option key={t.key} value={t.key}>{t.label}</option>
          ))}
        </select>
      </div>
      <div className="col-span-2">
        <Input
          data-testid={`${testIdPrefix}-value`}
          value={valueValue || ''}
          required={required}
          placeholder={meta.placeholder}
          onChange={(e) => onValueChange(e.target.value)}
        />
      </div>
    </div>
  );
};

export default TaxIdInput;
